import { useEffect, useRef, type JSX } from 'react';
import type { ChatMessage } from '../../chatbot.types';
import { ChatBubble } from '../chat-bubble';

type Props = {
  messages: ChatMessage[];
  onSelectOption: (messageId: string, optionId: string) => void;
  onSelectOptions: (messageId: string, optionIds: string[]) => void;
};

export const ChatMessageList = ({ messages, onSelectOption, onSelectOptions }: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Debounce: cancel pending scroll if messages update rapidly
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);

    // Defer scroll until after React + browser have fully committed the DOM
    scrollTimerRef.current = setTimeout(() => {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }, 50);

    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [messages]);

  return (
    <div ref={containerRef} className='chatbot-messages'>
      <div className='chatbot-messages-inner'>
        {messages.map((message) => (
          <ChatBubble
            key={message.id}
            message={message}
            onSelectOption={onSelectOption}
            onSelectOptions={onSelectOptions}
          />
        ))}
      </div>
    </div>
  );
};
